import React, { useState } from 'react';
import {
  createStyles, Header, Container, Group, Burger, Paper, Transition, Button, Title, MediaQuery,
  useMantineTheme, Box,
} from '@mantine/core';
import { IconLanguage } from '@tabler/icons';
import { Link } from 'react-router-dom';
import { useBooleanToggle } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Logo } from '../logoJT.svg';

const HEADER_HEIGHT = 60;

const useStyles = createStyles((theme) => ({
  root: {
    position: 'sticky',
    zIndex: 120,
  },

  dropdown: {
    position: 'absolute',
    top: HEADER_HEIGHT,
    left: 0,
    right: 0,
    zIndex: 0,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    borderTopWidth: 0,
    overflow: 'hidden',
    height: 'fit-content',

    [theme.fn.largerThan('md')]: {
      display: 'none',
    },
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '100%',
    maxWidth: '100%',
  },

  languages: {
    flexWrap: 'nowrap',
    [theme.fn.smallerThan('md')]: {
      display: 'none',
    },
  },

  burger: {
    [theme.fn.largerThan('md')]: {
      display: 'none',
    },
  },

  language: {
    display: 'block',
    lineHeight: 1,
    padding: '8px 12px',
    borderRadius: theme.radius.sm,
    textDecoration: 'none',
    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,

    '&:hover': {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
    },

    [theme.fn.smallerThan('sm')]: {
      borderRadius: 0,
      width: '100%',
    },
  },

  languageActive: {
    '&, &:hover': {
      backgroundColor:
        theme.colorScheme === 'dark'
          ? theme.fn.rgba(theme.colors[theme.primaryColor][9], 0.25)
          : theme.colors[theme.primaryColor][0],
      color: theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 3 : 7],
    },
  },

  logo: {
    height: '40px',
    width: '40px',
    translate: '4px 4px',
    boxShadow: '4px 4px 0 goldenrod',
    borderRadius: '10px',
    '&:hover': {
      boxShadow: '6px 6px 0 goldenrod',
      translate: '2px 2px',
      transitionDuration: '0.1s',
      '&:active': {
        boxShadow: '0 0 0 goldenrod',
        translate: '9px 9px',
      },
    },
    marginRight: '10px',
    [theme.fn.largerThan('sm')]: {
      marginInline: '100px',
    },
  },
  languageIcon: {
    margin: '0 4px 0 12px',
  },
}));

interface HeaderResponsiveProps {
  languages: { name: string; code: string }[];
  navbarOpened: boolean;
  setNavbarOpened: Function;
}

export default function HeaderResponsive(
  { languages, navbarOpened, setNavbarOpened }: HeaderResponsiveProps,
) {
  const theme = useMantineTheme();
  const [langDropdownOpen, setLangDropdownOpen] = useBooleanToggle(false);
  const [activeLang, setActiveLang] = useState(languages[0]?.name);
  const { classes, cx } = useStyles();
  const { t, i18n } = useTranslation();

  const languageItems = languages.map((language) => (
    <Button
      key={language.code}
      type="button"
      className={cx(classes.language, { [classes.languageActive]: activeLang === language.name })}
      onClick={(event: { preventDefault: () => void; }) => {
        event.preventDefault();
        setActiveLang(language.name);
        setLangDropdownOpen(false);
        i18n.changeLanguage(language.code);
      }}
    >
      {language.name}
    </Button>
  ));

  return (
    <Header height={HEADER_HEIGHT} className={classes.root}>
      <Container className={classes.header}>
        <MediaQuery largerThan="md" styles={{ display: 'none' }}>
          <Burger
            opened={navbarOpened}
            onClick={() => setNavbarOpened((o: boolean) => !o)}
            className={classes.burger}
            size="sm"
            color={theme.colors.gray[6]}
            mr="xl"
          />
        </MediaQuery>
        <Box sx={{
          display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%',
        }}
        >
          <Link to="/" className="no-link-decoration">
            <Logo className={classes.logo} />
          </Link>
          <Title order={3}>{t('title')}</Title>
          <Box />
        </Box>
        <Group spacing={5} className={classes.languages}>
          {languageItems}
        </Group>

        <MediaQuery largerThan="md" styles={{ display: 'none' }}>
          <IconLanguage
            color={theme.colors.gray[6]}
            width="32px"
            onClick={() => setLangDropdownOpen()}
            className={classes.languageIcon}
          />
        </MediaQuery>

        <Transition transition="slide-down" duration={200} mounted={langDropdownOpen}>
          {(styles) => (
            <Paper className={classes.dropdown} withBorder style={styles}>
              {languageItems}
            </Paper>
          )}
        </Transition>
      </Container>
    </Header>
  );
}
